import React, { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import api from "../lib/apiClient";
import { Search, Loader2, FileText, Filter } from "lucide-react";
import { Input } from "../components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";

const STATUS_STYLE = {
  pending: "bg-blue-50 text-blue-700",
  draft: "bg-slate-100 text-slate-600",
  in_progress: "bg-amber-50 text-amber-700",
  completed: "bg-emerald-50 text-emerald-700",
  cancelled: "bg-red-50 text-red-600",
};

export default function History() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const [referrals, setReferrals] = useState([]);
  const [centres, setCentres] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState(params.get("q") || "");
  const [centre, setCentre] = useState("all");
  const [status, setStatus] = useState("all");

  const load = () => {
    setLoading(true);
    const q = {};
    if (search) q.search = search;
    if (centre !== "all") q.centre = centre;
    if (status !== "all") q.status = status;
    api.get("/referrals", { params: q }).then((r) => setReferrals(r.data)).finally(() => setLoading(false));
  };

  useEffect(() => { api.get("/centres").then((r) => setCentres(r.data)); }, []);
  useEffect(() => { const t = setTimeout(load, 250); return () => clearTimeout(t); }, [search, centre, status]);

  return (
    <div className="p-6 md:p-8 max-w-6xl mx-auto">
      <h1 className="text-3xl font-bold tracking-tight text-slate-900 font-head">Referral History</h1>
      <p className="text-slate-500 mt-1">Search, filter and reuse your past referrals.</p>

      <div className="flex flex-wrap gap-3 mt-6">
        <div className="relative flex-1 min-w-[220px]">
          <Search className="w-4 h-4 absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400" />
          <Input data-testid="history-search" value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Patient name or referral number…" className="pl-10 h-12 rounded-xl" />
        </div>
        <Select value={centre} onValueChange={setCentre}>
          <SelectTrigger data-testid="filter-centre" className="h-12 rounded-xl w-full sm:w-56"><SelectValue placeholder="Centre" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All centres</SelectItem>
            {centres.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={status} onValueChange={setStatus}>
          <SelectTrigger data-testid="filter-status" className="h-12 rounded-xl w-full sm:w-44"><SelectValue placeholder="Status" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All statuses</SelectItem>
            <SelectItem value="draft">Draft</SelectItem>
            <SelectItem value="pending">Pending</SelectItem>
            <SelectItem value="in_progress">In progress</SelectItem>
            <SelectItem value="completed">Completed</SelectItem>
            <SelectItem value="cancelled">Cancelled</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <div className="bg-white rounded-2xl border border-slate-200 shadow-sm mt-6 overflow-hidden">
        {loading ? (
          <div className="flex justify-center py-20"><Loader2 className="w-7 h-7 animate-spin text-blue-600" /></div>
        ) : referrals.length === 0 ? (
          <div className="p-16 text-center text-slate-400"><FileText className="w-10 h-10 mx-auto mb-3" /><p>No referrals match your filters.</p></div>
        ) : (
          <div className="divide-y divide-slate-100">
            {referrals.map((r) => (
              <button key={r.id} data-testid={`history-row-${r.id}`} onClick={() => navigate(`/referrals/${r.id}`)} className="w-full flex items-center justify-between p-4 hover:bg-slate-50 text-left transition-colors">
                <div className="min-w-0">
                  <p className="font-semibold text-slate-900 truncate">{r.patient?.first_name} {r.patient?.last_name}</p>
                  <p className="text-sm text-slate-500 truncate">{r.studies?.map((s) => s.name).join(", ")}</p>
                  <p className="text-xs text-slate-400 mt-0.5">{r.centre_snapshot?.name || "No centre"} · {r.referral_number}</p>
                </div>
                <div className="flex items-center gap-3 shrink-0">
                  <span className={`text-xs font-medium px-2.5 py-1 rounded-full ${STATUS_STYLE[r.status]}`}>{r.status}</span>
                  <span className="text-xs text-slate-400 hidden sm:block">{r.created_at?.slice(0, 10)}</span>
                </div>
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
